/**
 * @fileoverview Preview-pane illustration: the three preview states (Phase 5, J3).
 * @module ui-admin/help/illustrations/IllustrationPreviewPane
 *
 * Responsibilities: - Faithfully recreate the preview pane in its three states: normal split,
 *                     collapsed strip, and full-screen overlay, including the drag handle.
 * Dependencies:     - React; shared illustration primitives.
 * Architecture:     - Presentation-only SVG. No state, no data, no side effects.
 * Notes:            - Mirrors the resizable preview pane built in Phase 2 (H2).
 */

import React from 'react';
import { CalloutBadge, IllustrationFigure, ILLUS } from './illustrationPrimitives';

/**
 * @component IllustrationPreviewPane
 * Purpose: Annotated diagram of the preview pane in normal, collapsed, and full-screen states.
 */
export function IllustrationPreviewPane() {
  return (
    <IllustrationFigure
      title="The preview pane in its normal, collapsed, and full-screen states"
      viewBox="0 0 400 150"
      legend={[
        'Normal split: canvas on the left, preview on the right.',
        'Drag handle: drag to resize, or drag fully for full-screen.',
        'Collapsed strip: preview minimised to reclaim canvas width.',
        'Full-screen overlay: the preview fills the workspace.',
      ]}
    >
      <rect x={2} y={2} width={396} height={146} rx={8} fill={ILLUS.white} stroke={ILLUS.gray200} />

      {/* Normal split */}
      <rect x={16} y={20} width={120} height={110} rx={5} fill={ILLUS.gray100} stroke={ILLUS.gray200} />
      <rect x={22} y={26} width={62} height={98} rx={3} fill={ILLUS.white} stroke={ILLUS.gray300} />
      <text x={53} y={20} textAnchor="middle" fontSize={7.5} fill={ILLUS.gray500}>Canvas</text>
      <rect x={90} y={26} width={40} height={98} rx={3} fill={ILLUS.white} stroke={ILLUS.gray300} />
      {/* drag handle */}
      <rect x={85} y={60} width={4} height={28} rx={2} fill={ILLUS.orange} />

      {/* Collapsed strip */}
      <rect x={156} y={20} width={100} height={110} rx={5} fill={ILLUS.gray100} stroke={ILLUS.gray200} />
      <rect x={162} y={26} width={80} height={98} rx={3} fill={ILLUS.white} stroke={ILLUS.gray300} />
      <rect x={244} y={26} width={8} height={98} rx={2} fill={ILLUS.gray200} />
      <text x={206} y={20} textAnchor="middle" fontSize={7.5} fill={ILLUS.gray500}>Collapsed</text>

      {/* Full-screen */}
      <rect x={276} y={20} width={108} height={110} rx={5} fill={ILLUS.gray100} stroke={ILLUS.gray200} />
      <rect x={282} y={26} width={96} height={98} rx={3} fill={ILLUS.white} stroke={ILLUS.gray300} />
      <rect x={290} y={34} width={80} height={8} rx={2} fill={ILLUS.gray200} />
      <rect x={290} y={46} width={56} height={6} rx={2} fill={ILLUS.gray200} />
      <rect x={290} y={56} width={80} height={6} rx={2} fill={ILLUS.gray100} />
      <text x={330} y={20} textAnchor="middle" fontSize={7.5} fill={ILLUS.gray500}>Full-screen</text>

      {/* Callouts */}
      <CalloutBadge n={1} x={53} y={34} />
      <CalloutBadge n={2} x={87} y={52} />
      <CalloutBadge n={3} x={248} y={34} />
      <CalloutBadge n={4} x={330} y={34} />
    </IllustrationFigure>
  );
}
